/**
 * 打ち上げ一覧の「直答ブロック＋全件テーブル」を組み立てる純粋関数（DOM/Worker非依存）。
 *
 * ★なぜ共有レンダラなのか（2026-07-31）
 *  トップは `renderTop()` が `#app` を innerHTML で**全置換**する。したがって
 *  「静的HTMLに一覧を焼く」だけではJS実行の瞬間に消え、レンダ後DOMを見るGoogleには届かない。
 *  逆に静的とレンダ後で中身が食い違えば、index.html 自身が課している
 *  「静的初期HTMLとレンダ後DOMを食い違わせない」規律に反する。
 *  → ビルド時（scripts/build-site-pages.ts が同梱スナップショットから生成）と
 *    実行時（src/app.ts が live データから生成）の**両方がこの関数を通る**ようにして、
 *    定義上一致させる。
 *
 * ★日付は必ず format.ts の formatLaunchDate を通す
 *  LL2は日程未定の回に「その年の12/31」「その月の月末」という**仮の net** を入れてくる。
 *  生の net を出すと「12月31日」が5行並ぶ嘘の一覧になる。formatLaunchDate は
 *  精度に応じて「2026年内・時期未定」等へ丸めるので、これを唯一の表示経路にする。
 */
import { formatLaunchDate, precisionInfo, jstDate } from './format';

/** この関数群が必要とする打ち上げの最小形（LaunchDTO が構造的に満たす）。 */
export interface LaunchSummaryItem {
  id: string;
  name: string;
  rocket: string;
  mission: string | null;
  net: string;
  netPrecision: string;
  siteId: string | null;
  padName?: string | null;
}

export interface SiteLabel {
  /** 一覧に出す短い射場名（「（吉信射点 LA-Y）」等の括弧書きを落としたもの） */
  short: string;
}

export type ResolveSiteLabel = (siteId: string | null) => SiteLabel | null;

const E = (s: string): string =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * ミッション名の表示形。和名があれば英語名を併記するが、
 * **和名の中に既に英語の略号が入っている場合は併記しない**
 * （「技術試験衛星9号機（ETS-9）（ETS-9）」のような二重括弧を防ぐ）。
 */
export function displayMission(missionEn: string, jaMission?: string | null): string {
  if (!jaMission) return missionEn;
  // 「Martian Moon eXplorer (MMX)」→ 略号 "MMX"
  const abbrev = /\(([^()]+)\)\s*$/.exec(missionEn)?.[1] ?? null;
  const alreadyHasEnglish = jaMission.includes(missionEn) || (abbrev !== null && jaMission.includes(abbrev));
  return alreadyHasEnglish ? jaMission : `${jaMission}（${missionEn}）`;
}

/**
 * 打ち上げの表示名（「H3-22／みちびき7号機」）。和名があれば併記する。
 * 日本語の検索者は和名で探すが、英語名も一次情報との突合に要るので消さない。
 * @param sep 機体とミッションの区切り。表では「｜」、文中では「／」を使う。
 */
export function displayVehicle(l: LaunchSummaryItem, jaMission?: string | null, sep = '｜'): string {
  const rocket = l.rocket || l.name.split('|')[0]?.trim() || '';
  const missionEn = l.mission || l.name.split('|')[1]?.trim() || '';
  if (!missionEn) return rocket || '—';
  return `${rocket}${sep}${displayMission(missionEn, jaMission)}`;
}

/**
 * 一覧の並び順。
 *
 * ⚠️ 2026-07-31の是正: 従来は「確度が粗い回を無条件で末尾へ」送っていたため、
 *    2026年内の Y精度5件（ETS-9 / HTV-X2 / HTV-X3 / LUPEX / LOTUSat-1）が
 *    2028年の回より後ろに並び、トップの先頭4件から押し出されていた（実測）。
 *    「今年の打ち上げ予定」を知りたい人にとって、これは並び順のバグ。
 *    → まず年で並べ、同じ年の中でだけ「日付が意味を持つ回を先に」を適用する。
 */
export function sortForSchedule<T extends LaunchSummaryItem>(launches: T[]): T[] {
  const year = (l: T): number => jstDate(l.net)?.y ?? 9999;
  const rank = (l: T): number => {
    const t = precisionInfo(l.netPrecision).tier;
    return t === 'year' || t === 'tbd' ? 1 : 0;
  };
  return [...launches].sort(
    (a, b) => year(a) - year(b) || rank(a) - rank(b) || (Date.parse(a.net) || 0) - (Date.parse(b.net) || 0),
  );
}

/** 年ごとの件数（本文に書く数字は必ずここで機械集計する＝手書きしない）。 */
export function countByYear(launches: LaunchSummaryItem[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const l of launches) {
    const y = jstDate(l.net)?.y;
    if (y == null) continue;
    m.set(y, (m.get(y) ?? 0) + 1);
  }
  return m;
}

export interface Headline {
  kind: 'confirmed' | 'coarse' | 'none';
  /** 「次の打ち上げ」＝並び順で最初の回。確度は問わない。 */
  launch: LaunchSummaryItem | null;
  /**
   * kind==='coarse' のとき、日付まで確定している直近の回（無ければ null）。
   * 「次はいつ」と「次に日付が決まっているのはいつ」は別の問いなので、混ぜずに両方出す。
   */
  nextConfirmed: LaunchSummaryItem | null;
}

/**
 * 「次の国内ロケット打ち上げはいつ？」への直答に使う回を選ぶ。
 *
 * ⚠️ 2026-08-04 の是正（重要）
 *   従来は「日付が確定している回を優先」＝ sorted.find(bookable) を直答にしていた。
 *   これは **日程未定の回を飛ばして、ずっと先の確定回を「次の打ち上げ」と言ってしまう**。
 *   実データで、2026年8月（日にち未定）と2026年10月の回を飛ばして
 *   「次の国内ロケット打ち上げは 2028年4月20日」と表示していた（実測）。
 *   打ち上げが先にある以上、これは事実として誤り。
 *   AI検索や検索スニペットはこの一文をそのまま引用するので、実害が大きい。
 *
 *   → 直答は常に**並び順で最初の回**（＝次に来る回）。確度はラベルで正直に示す。
 *     日付が確定している直近の回は別途 nextConfirmed で返し、文を分けて併記する。
 */
export function pickHeadline(launches: LaunchSummaryItem[]): Headline {
  const sorted = sortForSchedule(launches);
  const first = sorted[0];
  if (!first) return { kind: 'none', launch: null, nextConfirmed: null };
  const confirmed = sorted.find((l) => precisionInfo(l.netPrecision).bookable) ?? null;
  if (confirmed && confirmed === first) {
    return { kind: 'confirmed', launch: first, nextConfirmed: null };
  }
  return { kind: 'coarse', launch: first, nextConfirmed: confirmed };
}

/** 取得時刻を「2026年7月31日 09:25」形式（JST）にする。 */
export function fmtCapturedAt(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const p = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
  return p;
}

/**
 * 直答ブロック。「次の国内ロケット打ち上げはいつですか？」に対する短い答えを、
 * 検索エンジンにも人にも同じ文で返す。
 *
 * ⚠️ 取得時刻は文の中に織り込む（data-nosnippet で隠さない）。
 *    唯一の免責である「いつ時点の情報か」がスニペットから消えると、
 *    SERP上では断定に見えてしまう。
 */
export function renderAnswerHtml(
  launches: LaunchSummaryItem[],
  capturedAt: string,
  resolveSite: ResolveSiteLabel,
  jaMission?: (l: LaunchSummaryItem) => string | null,
): string {
  const head = pickHeadline(launches);
  const at = fmtCapturedAt(capturedAt);
  const byYear = countByYear(launches);
  const thisYear = jstDate(capturedAt)?.y;
  const thisYearCount = thisYear != null ? (byYear.get(thisYear) ?? 0) : 0;
  // ⚠️ 件数は必ず「同じ年の中」で数える。全件から数えると、2028年の回を
  //    「2026年内のうち確定しているのは…」に混ぜて数えてしまう（実際に一度やった）。
  const thisYearConfirmed =
    thisYear == null
      ? 0
      : launches.filter((l) => jstDate(l.net)?.y === thisYear && precisionInfo(l.netPrecision).bookable).length;

  let answer: string;
  if (head.kind === 'none' || !head.launch) {
    answer = `${E(at)}時点で、The Space Devs Launch Library 2 に登録されている国内の今後の打ち上げ予定はありません。`;
  } else {
    const l = head.launch;
    const d = formatLaunchDate(l.net, l.netPrecision);
    const site = resolveSite(l.siteId);
    // 文中なので機体とミッションの区切りは「／」（表の「｜」だと読み下しにくい）
    const veh = displayVehicle(l, jaMission?.(l) ?? null, '／');
    const when =
      head.kind === 'confirmed'
        ? `<strong>${E(d.main)}${d.sub ? ` ${E(d.sub)}` : ''}</strong>`
        : `<strong>${E(d.main)}</strong>（${E(d.sub || '日付未定')}）`;
    const where = site ? `${E(site.short)}からの` : '';
    const tail = head.kind === 'confirmed' ? 'です。' : 'の見込みです。';
    answer = `${E(at)}時点の予定では、次の国内ロケット打ち上げは ${when}、${where}<strong>${E(veh)}</strong> ${tail}`;

    // 次の回の日程が粗いときは、「日付まで決まっている直近の回」を別の文で足す。
    // ここを1文に混ぜると、先に来る未定の回を飛ばして遠い確定回を「次」と言うことになる。
    if (head.kind === 'coarse' && head.nextConfirmed) {
      const c = head.nextConfirmed;
      const cd = formatLaunchDate(c.net, c.netPrecision);
      const cs = resolveSite(c.siteId);
      const cv = displayVehicle(c, jaMission?.(c) ?? null, '／');
      answer +=
        `日付まで決まっている直近の回は <strong>${E(cd.main)}${cd.sub ? ` ${E(cd.sub)}` : ''}</strong>、` +
        `${cs ? `${E(cs.short)}からの` : ''}<strong>${E(cv)}</strong> です。`;
    }
  }

  const secondLine =
    thisYear != null && thisYearCount > 0
      ? `<p class="ans-sub">${thisYear}年内に予定されている国内の打ち上げは${thisYearCount}件で、そのうち日付まで決まっているのは${thisYearConfirmed}件、残り${thisYearCount - thisYearConfirmed}件は「年内」「月内」までしか決まっていません。日本の打ち上げは延期が頻発するため、確定日は各運用機関の公式発表をご確認ください。</p>`
      : `<p class="ans-sub">日本の打ち上げは延期が頻発するため、確定日は各運用機関の公式発表をご確認ください。</p>`;

  return `
  <section class="section answer-block" id="next-launch">
    <h2 class="section-h">次の国内ロケット打ち上げはいつ？</h2>
    <p class="ans-lead">${answer}</p>
    ${secondLine}
  </section>`;
}

/**
 * 打ち上げ予定の全件テーブル。
 * @param siteId 指定するとその射場だけに絞る（射場ガイドページ用）
 */
export function renderScheduleTableHtml(
  launches: LaunchSummaryItem[],
  capturedAt: string,
  resolveSite: ResolveSiteLabel,
  opts: {
    siteId?: string;
    heading?: string;
    jaMission?: (l: LaunchSummaryItem) => string | null;
    /** 射場ガイドページは .legal-section / .legal-h2 系のクラスを使うので差し替えられるようにする */
    sectionClass?: string;
    headingClass?: string;
    /** 射場ページでは「国内4射場の」という説明が誤りになるので上書きする */
    intro?: string;
    /** 0件のときの説明文（射場ページは一次情報の案内に差し替える） */
    emptyNote?: string;
  } = {},
): string {
  const list = sortForSchedule(opts.siteId ? launches.filter((l) => l.siteId === opts.siteId) : launches);
  const at = fmtCapturedAt(capturedAt);
  const heading = opts.heading ?? '国内ロケット打ち上げ予定の一覧';
  const secCls = opts.sectionClass ?? 'section';
  const hCls = opts.headingClass ?? 'section-h';

  if (!list.length) {
    const note =
      opts.emptyNote ??
      `${at}時点で、The Space Devs Launch Library 2 に登録されている今後の打ち上げ予定はありません。打ち上げ日は各運用機関の公式発表が一次情報です。`;
    return `
  <section class="${E(secCls)}">
    <h2 class="${E(hCls)}">${E(heading)}</h2>
    <p class="ctx">${E(note)}</p>
  </section>`;
  }

  const rows = list
    .map((l) => {
      const d = formatLaunchDate(l.net, l.netPrecision);
      const p = precisionInfo(l.netPrecision);
      const site = resolveSite(l.siteId);
      const veh = displayVehicle(l, opts.jaMission?.(l) ?? null);
      return `
      <tr>
        <td data-label="日程"><span class="sch-date">${E(d.main)}</span>${d.sub ? `<span class="sch-sub">${E(d.sub)}</span>` : ''}</td>
        <td data-label="確度"><span class="sch-conf tone-${E(p.tone)}">${E(p.label)}</span></td>
        <td data-label="機体・ミッション">${E(veh)}</td>
        <td data-label="射場">${E(site?.short ?? '—')}</td>
      </tr>`;
    })
    .join('');

  const intro =
    opts.intro ??
    `The Space Devs Launch Library 2 に登録されている国内4射場の打ち上げ予定です（${at}時点）。登録が無い回は表示されません。日程が「年内」「時期未定」の回は、その年内・その時期という以上のことは決まっていません。`;

  return `
  <section class="${E(secCls)}">
    <h2 class="${E(hCls)}">${E(heading)}</h2>
    <p class="ctx">${E(intro)}</p>
    <div class="sch-wrap">
      <table class="sch-table">
        <thead><tr><th>日程</th><th>確度</th><th>機体・ミッション</th><th>射場</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  </section>`;
}
